import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  Divider,
  Drawer,
  FormControl,
  Grid,
  IconButton,
  Link,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
  useTheme,
  Tooltip,
} from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { CWIExplain } from 'fs_design_system'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import {
  BarChartRounded,
  CalendarMonthRounded,
  HelpOutlineRounded,
  TableViewRounded,
} from '@mui/icons-material'
import { isArray } from 'lodash'
import FsAlert, { FsAlertRef } from '../../components/common/atoms/Alert'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { setPtKPIsForCurrentRun, setPtRuns } from '../../store/actions'
import IconTooltip from '../../components/icon-tooltip/icon-tooltip'
import FsDemoAccountModal from '../../components/common/molecules/DemoAccountModal'
import { getIsDemoAccount } from '../../utils'
import { downloadKPIsAsPDF } from './fs-pt-kpis-pdf'
import SectionNavigatorDropdown from '../../components/fs-pt-kpis/SectionNavigator'
import DropdownButton from '../../components/fs-pt-kpis/DropdownButton'
import TableView from '../../components/fs-pt-kpis/TableView'
import ChartView from '../../components/fs-pt-kpis/ChartView'
import { getIcon } from '../../components/common/Icons'
import FsFullPageLoader from '../../components/common/atoms/FullPageLoader'
import FsLeftInfoDrawer from '../../components/common/atoms/LeftInfoDrawer'
import LoadingDash from '../../components/common/atoms/LoadingDash'

const FsPtKPIsView = () => {
  const { t } = useTranslation()
  const [running, setRunning] = useState<boolean>(false)
  const [currentRunToShow, setCurrentRunToShow] = useState<string | null>(null)
  const theme = useTheme()
  const { currentPtRun, ptKPIs, ptRuns } = useAppSelector((state) => state.currentPt)
  const { currentLanguage } = useAppSelector((state) => state.currentUser)
  const dispatch = useAppDispatch()
  const [openDemoModal, setOpenDemoModal] = useState<boolean>(false)
  const [helpOpen, setHelpOpen] = useState(false)
  const [cwiHelpOpen, setCwiHelpOpen] = useState(false)
  const [isTableView, setIsTableView] = useState<boolean>(false)
  const handleHelpOpen = () => setHelpOpen(true)
  const handleHelpClose = () => {
    setHelpOpen(false)
    setCwiHelpOpen(false)
  }

  const alertRef = useRef<FsAlertRef>(null)

  useEffect(() => {
    dispatch(setPtRuns())
  }, [])

  useEffect(() => {
    if (currentPtRun) setCurrentRunToShow(`${currentPtRun.id}`)
  }, [currentPtRun])

  useEffect(() => {
    if (currentRunToShow) {
      if (!ptKPIs?.[currentRunToShow]) {
        dispatch(setPtKPIsForCurrentRun({ runId: parseInt(currentRunToShow, 10) }))
      }
    }
  }, [currentRunToShow])

  const latestRun = ptRuns ? ptRuns[ptRuns.length - 1] : null

  const filteredRuns = ptRuns ? ptRuns?.filter((r) => r !== null || r !== undefined) : []

  const isDemoAccount = getIsDemoAccount()

  const downloadKPIs = () => {
    if (isDemoAccount) {
      setOpenDemoModal(true)
      return
    }
    try {
      setRunning(true)
      if (!currentRunToShow || !ptKPIs || !ptKPIs[currentRunToShow]) return

      let maxVars = 0
      const rows = ptKPIs[currentRunToShow]
        .map((etlKPI: any) => {
          return etlKPI.sections
            .map((section: any) => {
              if (section.results.length === 0) return ''
              return section.results.map((res: any) => {
                if (isArray(res.value)) {
                  if (res.value.length > maxVars) maxVars = res.value.length
                  let resStr = `"${section.law}","${section.title.text[currentLanguage]}","${t(
                    res.label.text,
                  )}${
                    res.label.tooltipText
                      ? ` (${t('etrl.kpi.occ.inc')} (${t(
                          'pt.assistance.selected.occ.kldb',
                        )}): ${res.label.tooltipText.map((ttt: any) => ttt.kldb20105).join(', ')})`
                      : ''
                  }"`
                  res.value.forEach((rv: any) => {
                    resStr = `${resStr},"${t(`etrl.kpi.${rv.label}` as any)}","${
                      rv.value !== null
                        ? `${Number(rv.value).toLocaleString(currentLanguage, {
                            maximumFractionDigits: 2,
                          })} %`
                        : 'NA'
                    }"`
                  })
                  return resStr
                }
                return `"${section.law}","${section.title.text[currentLanguage]}","${t(
                  res.label.text,
                )}${
                  res.label.tooltipText
                    ? ` (${t('etrl.kpi.occ.inc')}: ${res.label.tooltipText[currentLanguage].join(
                        ', ',
                      )})`
                    : ''
                }",,"${
                  res.value !== null
                    ? `${Number(res.value).toLocaleString(currentLanguage, {
                        maximumFractionDigits: 2,
                      })} %`
                    : 'NA'
                }"`
              })
            })
            .filter((r: any) => r)
            .flat()
        })
        .filter((secArr: any) => secArr.length !== 0)
        .flat()
        .join('\n')
      let headers = `${t('etrl.kpi.csv.header.law.title')},${t('etrl.kpi.csv.header.law.name')},${t(
        'etrl.kpi.csv.header.kpi.name',
      )}`
      Array(maxVars)
        .fill(0)
        .forEach(() => {
          headers = `${headers},${t('etrl.kpi.csv.header.kpi.type')},${t(
            'etrl.kpi.csv.header.kpi.value',
          )}`
        })
      headers = `${headers}\n`

      const blob = new Blob([headers + rows], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.setAttribute('download', 'EU_KPIs.csv')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch {
      alertRef.current?.openAlert({ severity: 'error', message: t('something.went.wrong') })
    } finally {
      setRunning(false)
    }
  }

  const downloadPDFKPIs = async () => {
    if (isDemoAccount) {
      setOpenDemoModal(true)
      return
    }
    try {
      setRunning(true)
      if (!currentRunToShow || !ptKPIs || !ptKPIs[currentRunToShow] || !ptRuns) return

      await downloadKPIsAsPDF(
        ptKPIs[currentRunToShow],
        ptRuns.filter((r) => r.id === parseInt(currentRunToShow, 10))[0].year,
        currentLanguage,
        theme,
        t,
      )
    } catch {
      alertRef.current?.openAlert({ severity: 'error', message: t('something.went.wrong') })
    } finally {
      setRunning(false)
    }
  }

  const kpisToShow =
    ptKPIs && currentRunToShow && ptKPIs[currentRunToShow]
      ? isDemoAccount
        ? Object.values(ptKPIs[currentRunToShow]).slice(0, 1)
        : Object.values(ptKPIs[currentRunToShow])
      : null

  const scrollSections = kpisToShow
    ? kpisToShow
        .map((type: any) => {
          const sections = isDemoAccount ? type.sections.slice(0, 2) : type.sections
          return sections.map((ss: any) => ({ id: `kpi_${type.id}_${ss.id}`, title: ss.title }))
        })
        .flat()
    : []

  const scrollContainerRef = useRef<HTMLDivElement | null>(null)

  return (
    <div>
      <FsAlert ref={alertRef} />
      <FsDemoAccountModal isOpen={openDemoModal} setIsOpen={setOpenDemoModal} app="pt" />
      <Box>
        <Grid container>
          <Grid size={{ xs: 11.5 }}>
            <Stack mb={1} direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" alignItems="center">
                {getIcon('pt-kpis', { height: 32, width: 32 })}
                <Typography variant="h1" ml={1}>
                  {t('etrl.kpi.title')}
                </Typography>
                <Box pl={1} pt={0.75} onClick={handleHelpOpen} sx={{ cursor: 'pointer' }}>
                  <InfoOutlinedIcon sx={{ fontSize: 26, color: theme.palette.text.secondary }} />
                </Box>
              </Stack>
              <Stack direction="row" alignItems="center">
                <Tooltip
                  title={<Typography>{t('etrl.kpi.coming.soon')}</Typography>}
                  placement="bottom"
                  arrow
                >
                  <span>
                    <Button disabled variant="contained" sx={{ py: 1, mb: 1 }}>
                      {t('etrl.kpi.upload')}
                    </Button>
                  </span>
                </Tooltip>
              </Stack>
            </Stack>
          </Grid>
          <Grid size={{ xs: 0.5 }} alignItems="flex-end" display="flex" flexDirection="column" />
        </Grid>
        <Grid container>
          <Grid size={{ xs: 11.5 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack
                mb={4}
                direction="row"
                alignItems="center"
                width="90%"
                sx={{ flexWrap: 'wrap' }}
              >
                <Typography variant="h3" mr={1}>
                  {t('etrl.kpi.date')}
                </Typography>
                {latestRun && (
                  <FormControl size="small">
                    {ptRuns && ptRuns.length > 0 && (
                      <Select
                        value={`${currentRunToShow ?? ''}`}
                        id="run"
                        label="Run"
                        IconComponent={() => null}
                        onChange={(e: SelectChangeEvent) => {
                          if (isDemoAccount) {
                            setOpenDemoModal(true)
                            return
                          }
                          setCurrentRunToShow(e.target.value)
                        }}
                        style={{
                          width: 300,
                        }}
                        renderValue={(idr) => {
                          const rr = ptRuns.filter((r) => r.id === parseInt(idr, 10))[0]
                          return rr ? (
                            <Stack
                              flexDirection="row"
                              alignItems="center"
                              justifyContent="space-between"
                            >
                              <Typography>{`${rr.year}${
                                rr.id === latestRun.id ? ` (${t('latest.year')})` : ''
                              }`}</Typography>
                              <CalendarMonthRounded sx={{ mr: -3 }} />
                            </Stack>
                          ) : null
                        }}
                        notched={false}
                      >
                        {filteredRuns.reverse().map((run, index) => (
                          <MenuItem key={`${run.year}_${index}`} value={`${run.id}`}>{`${
                            run.year
                          } ${index === 0 ? ` (${t('latest.year')})` : ''}`}</MenuItem>
                        ))}
                      </Select>
                    )}
                  </FormControl>
                )}
              </Stack>

              <Stack
                direction="row"
                alignItems="flex-start"
                justifyContent="flex-end"
                width="50%"
                sx={{ flexWrap: 'wrap' }}
              >
                <Box
                  sx={{
                    border: `1px solid ${
                      !isTableView ? theme.palette.primary.main : theme.palette.action.disabled
                    }`,
                    '&:hover': {
                      borderColor: !isTableView
                        ? theme.palette.primary.main
                        : theme.palette.text.primary,
                      color: !isTableView ? theme.palette.primary.main : theme.palette.text.primary,
                    },
                    color: !isTableView
                      ? theme.palette.primary.main
                      : theme.palette.action.disabled,
                    borderTopLeftRadius: 4,
                    borderBottomLeftRadius: 4,
                    py: 0.25,
                    px: 1,
                    cursor: isTableView ? 'pointer' : '',
                  }}
                  onClick={() => setIsTableView(false)}
                >
                  <IconTooltip title={t('Chart View')} placement="bottom" arrow>
                    <Box pt={0.25}>
                      <BarChartRounded />
                    </Box>
                  </IconTooltip>
                </Box>
                <Box
                  sx={{
                    border: `1px solid ${
                      isTableView ? theme.palette.primary.main : theme.palette.action.disabled
                    }`,
                    '&:hover': {
                      borderColor: isTableView
                        ? theme.palette.primary.main
                        : theme.palette.text.primary,
                      color: isTableView ? theme.palette.primary.main : theme.palette.text.primary,
                    },
                    color: isTableView ? theme.palette.primary.main : theme.palette.action.disabled,
                    borderTopRightRadius: 4,
                    borderBottomRightRadius: 4,
                    py: 0.25,
                    px: 1,
                    mr: 1,
                    cursor: !isTableView ? 'pointer' : '',
                  }}
                  onClick={() => setIsTableView(true)}
                >
                  <IconTooltip title={t('Table View')} placement="bottom" arrow>
                    <Box pt={0.25}>
                      <TableViewRounded />
                    </Box>
                  </IconTooltip>
                </Box>

                <DropdownButton
                  disabled={running || !currentRunToShow}
                  onClick={(option: string) => {
                    if (option === 'csv') downloadKPIs()
                    else downloadPDFKPIs()
                  }}
                />
              </Stack>
            </Stack>
          </Grid>
          <Grid size={{ xs: 0.5 }} />
        </Grid>
      </Box>
      <Grid container mt={-2}>
        <Grid size={{ sm: 11.5 }}>
          {kpisToShow ? (
            <Box
              ref={scrollContainerRef}
              sx={{
                height: `${isDemoAccount ? 'calc(100vh - 556px)' : 'calc(100vh - 216px)'}`,
                overflowY: 'scroll',
                overflowX: 'hidden',
                border: '1px solid',
                borderRadius: 1,
                borderColor: 'secondary.light',
              }}
            >
              {kpisToShow.map((type: any) => {
                const sections = isDemoAccount ? type.sections.slice(0, 2) : type.sections
                return sections.map((section: any) => {
                  const { id, results, law, title, info } = section
                  let valueHeaders: string[] = []
                  if (results[0] && isArray(results[0].value)) {
                    valueHeaders = results[0].value.map((v: any) => t(`etrl.kpi.${v.label}` as any))
                  }
                  return (
                    <Box
                      key={`kpi_${type.id}_${section.id}`}
                      id={`kpi_${type.id}_${section.id}`}
                      sx={{ mb: 2 }}
                    >
                      <Box
                        sx={{
                          position: 'sticky',
                          top: -4,
                          zIndex: 10,
                        }}
                      >
                        <Box
                          p={2}
                          sx={{
                            backgroundColor: 'secondary.light',
                            borderBottom: '1px solid',
                            borderColor: 'secondary.light',
                          }}
                        >
                          <Typography variant="body2">{law}</Typography>
                          <Stack direction="row" alignItems="center">
                            <Typography variant="h3" className="my-2">
                              {title.text[currentLanguage]}
                              {law === 'Abs. 1 Ziff. 7 (EU Art. 9, 1. g)' && (
                                <IconButton onClick={() => setCwiHelpOpen(true)} sx={{ mb: 0.5 }}>
                                  <HelpOutlineRounded
                                    fontSize="small"
                                    sx={{ color: theme.palette.text.primary }}
                                  />
                                </IconButton>
                              )}
                            </Typography>
                          </Stack>
                        </Box>
                      </Box>
                      {isTableView ? (
                        <Box sx={{ overflowX: 'auto', mx: 2 }}>
                          <Box py={2} width="100%">
                            {valueHeaders.length > 0 && (
                              <Stack direction="row" justifyContent="space-between">
                                <Stack
                                  direction="row"
                                  alignItems="flex-start"
                                  mr={2}
                                  px={2}
                                  sx={{ minWidth: '200px' }}
                                >
                                  <Typography
                                    key={`type_${type.id}_section_${id}_info`}
                                    className="font-semibold"
                                  >
                                    {law === 'Abs. 1 Ziff. 7 (EU Art. 9, 1. g)'
                                      ? t('Comparable Worth Index (CWI)')
                                      : ''}
                                  </Typography>
                                </Stack>
                                <Stack direction="row" alignItems="flex-start" spacing={2}>
                                  {valueHeaders.map((vh, vhIndex) => (
                                    <Typography
                                      key={`type_${type.id}_section_${id}_${vhIndex}`}
                                      textAlign="center"
                                      className="font-semibold"
                                      sx={{ width: '200px', px: 2 }}
                                    >
                                      {vh}
                                    </Typography>
                                  ))}
                                </Stack>
                              </Stack>
                            )}
                          </Box>
                          <TableView results={results} type={type} id={id} law={law} />
                        </Box>
                      ) : (
                        <ChartView results={results} type={type} id={id} law={law} />
                      )}
                    </Box>
                  )
                })
              })}
            </Box>
          ) : (
            <Box sx={{ height: 'calc(100vh - 266px)', overflow: 'hidden', paddingTop: '50px' }}>
              <LoadingDash title={t('loading')} />
            </Box>
          )}
        </Grid>
        <Grid
          size={{ sm: 0.5 }}
          justifyContent="space-between"
          alignItems="flex-end"
          display="flex"
          flexDirection="column"
        >
          <SectionNavigatorDropdown
            sections={scrollSections}
            scrollContainerRef={scrollContainerRef}
          />
        </Grid>
      </Grid>
      {isDemoAccount && (
        <Box position="relative">
          <Box
            key="type_demo_section_demo"
            sx={{
              borderWidth: 1,
              borderStyle: 'solid',
              borderColor: 'secondary.main',
              mb: 2,
              minWidth: 876,
              borderRadius: 2,
              filter: 'blur(4px)',
            }}
          >
            <Box
              p={2}
              sx={{
                backgroundColor: 'secondary.light',
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
              }}
            >
              <Typography variant="subtitle2">Demo title</Typography>
              <Stack direction="row" alignItems="center">
                <Typography variant="h1">Demo title</Typography>
              </Stack>
            </Box>
            <Box p={2} pb={0}>
              <Stack
                key="type_demo_section_demo"
                direction="row"
                justifyContent="space-between"
                mb={2}
                borderRadius={2}
                sx={{ backgroundColor: 'action.hover' }}
              >
                <Stack
                  direction="row"
                  alignItems="flex-start"
                  mr={2}
                  p={2}
                  sx={{ minWidth: '200px' }}
                >
                  <Typography>Demo title</Typography>
                </Stack>
                <Stack direction="row" alignItems="flex-start" spacing={2}>
                  <Box
                    p={2}
                    key="res_demo_val"
                    sx={{
                      width: '200px',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderLeft: `4px solid ${theme.palette.background.paper}`,
                    }}
                  >
                    <Typography textAlign="center">89</Typography>
                  </Box>
                </Stack>
              </Stack>
              <Stack
                key="type_demo_section_demo"
                direction="row"
                justifyContent="space-between"
                mb={2}
                borderRadius={2}
                sx={{ backgroundColor: 'action.hover' }}
              >
                <Stack
                  direction="row"
                  alignItems="flex-start"
                  mr={2}
                  p={2}
                  sx={{ minWidth: '200px' }}
                >
                  <Typography>Demo title</Typography>
                </Stack>
                <Stack direction="row" alignItems="flex-start" spacing={2}>
                  <Box
                    p={2}
                    key="res_demo_val"
                    sx={{
                      width: '200px',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderLeft: `4px solid ${theme.palette.background.paper}`,
                    }}
                  >
                    <Typography textAlign="center">89</Typography>
                  </Box>
                </Stack>
              </Stack>
              <Stack
                key="type_demo_section_demo"
                direction="row"
                justifyContent="space-between"
                mb={2}
                borderRadius={2}
                sx={{ backgroundColor: 'action.hover' }}
              >
                <Stack
                  direction="row"
                  alignItems="flex-start"
                  mr={2}
                  p={2}
                  sx={{ minWidth: '200px' }}
                >
                  <Typography>Demo title</Typography>
                </Stack>
                <Stack direction="row" alignItems="flex-start" spacing={2}>
                  <Box
                    p={2}
                    key="res_demo_val"
                    sx={{
                      width: '200px',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderLeft: `4px solid ${theme.palette.background.paper}`,
                    }}
                  >
                    <Typography textAlign="center">89</Typography>
                  </Box>
                </Stack>
              </Stack>
            </Box>
          </Box>
          <Stack
            sx={{
              position: 'absolute',
              top: 0,
              backgroundColor: theme.palette.action.hoverOpacity,
              borderRadius: 2,
              boxShadow: `0px 1px 5px 0px ${theme.palette.text.secondary}`,
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: 24,
            }}
          >
            <Typography variant="h1" component="h2" mb={4} fontWeight="bold">
              {t('demo.account.title')}
            </Typography>
            <Typography mb={2} className="font-semibold" textAlign="center">
              {t('demo.account.description.full')}{' '}
              <Link href="mailto:support.pt@ines-analytics.com">support.pt@ines-analytics.com</Link>{' '}
              {t('demo.account.description.2')}
            </Typography>
            <Button
              variant="contained"
              sx={{
                mt: 4,
                width: 200,
              }}
            >
              <a
                href="mailto:support.pt@ines-analytics.com"
                style={{ color: 'white', textDecoration: 'none' }}
              >
                {t('demo.account.button.1')}
              </a>
            </Button>
          </Stack>
        </Box>
      )}

      {running && <FsFullPageLoader />}

      <FsLeftInfoDrawer open={helpOpen || cwiHelpOpen} onClose={handleHelpClose}>
        {helpOpen ? (
          <Typography component="div" style={{ whiteSpace: 'pre-line' }}>
            {t('etrl.kpi.desc')}
          </Typography>
        ) : (
          <Typography component="div" style={{ whiteSpace: 'pre-line' }}>
            <Typography variant="h3">{t('cwi_groups.exaplain')}</Typography>
            {CWIExplain[currentLanguage].content.map((e: any, eIndex: number) => (
              <Typography key={`cwi_${eIndex}`} sx={{ mt: 2 }}>
                {e}
              </Typography>
            ))}
            <Typography sx={{ mt: 2 }} fontWeight="bold">
              {t('literature')}:
            </Typography>
            {CWIExplain[currentLanguage].literature.map((e: any, eeIndex: number) => (
              <Typography key={`cwii_${eeIndex}`} sx={{ mt: 2 }}>
                {e}
              </Typography>
            ))}
          </Typography>
        )}
      </FsLeftInfoDrawer>
    </div>
  )
}

export default FsPtKPIsView
